import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import { AddCircleOutlineOutlined, DeleteOutline } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import { deleteRequest, getRequest, postRequest } from "Includes/api/apicall";
import { DEL_URL, GET_URL, POST_URL } from "Includes/urls";

function FeatureAdds(props) {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ module_name: "", url: "" });
    const [errors, setErrors] = useState({ module_name: "", url: "" });

    useEffect(() => {
        loadScreens();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize]);

    const loadScreens = () => {
        setLoading(true);
        getRequest(
            GET_URL.feature_screens.api,
            { limit: pageSize, pageno: page + 1 },
            props
        )
            .then((response) => {
                if (response && response.status === 200) {
                    const data = response.data.data || [];
                    setRows(data);
                    setRowCount(response.data?.count || data.length);
                }
                setLoading(false);
            })
            .catch(() => setLoading(false));
    };

    const openDialog = () => {
        setForm({ module_name: "", url: "" });
        setErrors({ module_name: "", url: "" });
        setDialogOpen(true);
    };

    const closeDialog = () => {
        if (saving) return;
        setDialogOpen(false);
    };

    const validate = () => {
        const next = { module_name: "", url: "" };
        if (!form.module_name.trim()) next.module_name = "Enter module name";
        if (!form.url.trim()) next.url = "Enter URL";
        setErrors(next);
        return !next.module_name && !next.url;
    };

    const saveScreen = () => {
        if (!validate()) return;
        setSaving(true);
        postRequest(
            POST_URL.feature_screens.api,
            {
                module_name: form.module_name.trim(),
                url: form.url.trim(),
            },
            { ...props, return_error: true }
        )
            .then((response) => {
                if (response && (response.status === 200 || response.status === 201)) {
                    setDialogOpen(false);
                    if (page !== 0) setPage(0);
                    else loadScreens();
                }
            })
            .finally(() => setSaving(false));
    };

    const deleteScreen = (row) => {
        deleteRequest(`${DEL_URL.feature_screens.api}${row.id}/`, {}, props).then((response) => {
            if (response && response.status === 200) {
                loadScreens();
            }
        });
    };

    const columns = [
        {
            field: "module_name",
            headerName: "Module name",
            flex: 1,
            minWidth: 220,
        },
        {
            field: "url",
            headerName: "URL",
            flex: 1.4,
            minWidth: 280,
        },
        {
            field: "actions",
            headerName: "",
            width: 150,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Box>
                    <Button
                        size="small"
                        onClick={() => navigate(`/basicdetails/feature-adds/edit/${params.row.id}`)}
                    >
                        Edit
                    </Button>
                    <IconButton
                        size="small"
                        aria-label="Delete screen"
                        onClick={() => deleteScreen(params.row)}
                    >
                        <DeleteOutline fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Grid container alignItems="center">
                <Grid item xs={12} md={6}>
                    <Typography variant="h5" fontWeight={600}>
                        Feature Adds
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Screens
                    </Typography>
                </Grid>
                <Grid item xs={12} md={6} textAlign="right">
                    <Button
                        variant="contained"
                        startIcon={<AddCircleOutlineOutlined />}
                        onClick={openDialog}
                        sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            px: 2,
                            py: 1,
                            boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
                        }}
                    >
                        Add
                    </Button>
                </Grid>
            </Grid>

            <Paper
                sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 3,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                }}
            >
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    autoHeight
                    rowHeight={44}
                    pagination
                    paginationMode="server"
                    page={page}
                    pageSize={pageSize}
                    onPageChange={(nextPage) => setPage(nextPage)}
                    onPageSizeChange={(nextPageSize) => {
                        setPageSize(nextPageSize);
                        setPage(0);
                    }}
                    pageSizeOptions={[10, 20, 50]}
                    rowCount={rowCount}
                    disableRowSelectionOnClick
                    sx={{
                        border: "none",
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: "#f5f7fb",
                            fontWeight: 600,
                        },
                        "& .MuiDataGrid-row:hover": {
                            backgroundColor: "#fafafa",
                        },
                        "& .MuiDataGrid-cell": {
                            borderBottom: "1px solid #f0f0f0",
                        },
                    }}
                />
            </Paper>

            <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
                <DialogTitle>Add screen</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Module name"
                        fullWidth
                        value={form.module_name}
                        error={Boolean(errors.module_name)}
                        helperText={errors.module_name}
                        onChange={(event) =>
                            setForm((prev) => ({ ...prev, module_name: event.target.value }))
                        }
                    />
                    <TextField
                        margin="dense"
                        label="URL"
                        fullWidth
                        placeholder="/module/path"
                        value={form.url}
                        error={Boolean(errors.url)}
                        helperText={errors.url}
                        onChange={(event) =>
                            setForm((prev) => ({ ...prev, url: event.target.value }))
                        }
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeDialog} disabled={saving}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={saveScreen} disabled={saving}>
                        {saving ? "Saving..." : "Add"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default FeatureAdds;
